import { BrowserRouter, Navigate, Route, Routes, useParams } from 'react-router';
import { ToastContainer } from 'react-toastify';
import './App.css';
import { AppVersionGate } from '@/components/app-version-gate';
import { FeatureFlagType, isEnabledFeature } from '@/components/feature-flag/types';
import { useDefaultMenuPath } from '@/hooks/use-default-menu-path';
import { GuestLayout, HeaderLayout } from './components/layout';
import { AuthProvider, ProtectedRoute } from './contexts/auth';
// サインイン画面は遅延読み込みしない。401 で /signin へハードリダイレクトする最中に
// ProtectedRoute がアプリ内でも /signin へ遷移するため、ここでチャンクを取りに行くと
// ページ遷移に打ち切られて Safari で読み込みエラーになる(HANLY-UI-PROD-3R)。
import { SignInPage } from './pages/auth/signin';
import { lazyPage } from './utils/lazy-page';

// ページは初回表示時に読み込む。静的に import すると全ページが初期バンドルに入り、
// 画面ごとにしか使わない重いライブラリ(PDF表示・バーコード読み取りなど)まで起動時に読み込んでしまう。
const PrivacyPolicyPage = lazyPage(() => import('@/pages/terms/privacy-policy'), 'PrivacyPolicyPage');
const ServiceListPage = lazyPage(() => import('@/pages/terms/service-list'), 'ServiceListPage');
const TermsOfServicePage = lazyPage(() => import('@/pages/terms/terms-of-service'), 'TermsOfServicePage');
const ResetPasswordPage = lazyPage(() => import('./pages/auth/password-change'), 'ResetPasswordPage');
const ForgotPasswordPage = lazyPage(() => import('./pages/auth/password-reset'), 'ForgotPasswordPage');
const RegisterPage = lazyPage(() => import('./pages/auth/register'), 'RegisterPage');
const ChatPage = lazyPage(() => import('./pages/chats'), 'ChatPage');
const CustomerDetailPage = lazyPage(() => import('./pages/customers/[id]'), 'CustomerDetailPage');
const CustomerActivitiesPage = lazyPage(
  () => import('./pages/customers/[id]/activities'),
  'CustomerActivitiesPage',
);
const CustomerContactPersonsPage = lazyPage(
  () => import('./pages/customers/[id]/contact-persons'),
  'CustomerContactPersonsPage',
);
const CustomerInfoPage = lazyPage(() => import('./pages/customers/[id]/customer-info'), 'CustomerInfoPage');
const CustomerBillingSettingsPage = lazyPage(
  () => import('./pages/customers/[id]/customer-info/billing-settings'),
  'CustomerBillingSettingsPage',
);
const CustomerDeliveryDestinationsPage = lazyPage(
  () => import('./pages/customers/[id]/customer-info/delivery-destinations'),
  'CustomerDeliveryDestinationsPage',
);
const CustomerDeliveryNoteEmailPage = lazyPage(
  () => import('./pages/customers/[id]/customer-info/delivery-note-email'),
  'CustomerDeliveryNoteEmailPage',
);
const CustomerModelNumbersPage = lazyPage(
  () => import('./pages/customers/[id]/customer-model-numbers'),
  'CustomerModelNumbersPage',
);
const CustomerDeliveryNoteTemplatesPage = lazyPage(
  () => import('./pages/customers/[id]/document-templates/delivery-note'),
  'CustomerDeliveryNoteTemplatesPage',
);
const CustomerPurchaseOrderTemplatesPage = lazyPage(
  () => import('./pages/customers/[id]/document-templates/purchase-order'),
  'CustomerPurchaseOrderTemplatesPage',
);
const CustomerDocumentsPage = lazyPage(
  () => import('./pages/customers/[id]/documents'),
  'CustomerDocumentsPage',
);
const CustomerProductPricesPage = lazyPage(
  () => import('./pages/customers/[id]/product-prices'),
  'CustomerProductPricesPage',
);
const CustomerSupplierInfoPage = lazyPage(
  () => import('./pages/customers/[id]/supplier-info'),
  'CustomerSupplierInfoPage',
);
const CustomerSupplierBankAccountsPage = lazyPage(
  () => import('./pages/customers/[id]/supplier-info/bank-accounts'),
  'CustomerSupplierBankAccountsPage',
);
const CustomerSupplierPaymentPage = lazyPage(
  () => import('./pages/customers/[id]/supplier-info/payment'),
  'CustomerSupplierPaymentPage',
);
const CustomerActivitiesListPage = lazyPage(
  () => import('./pages/customers/activities'),
  'CustomerActivitiesListPage',
);
const PartnerActivityImportPage = lazyPage(
  () => import('./pages/customers/activities/import'),
  'PartnerActivityImportPage',
);
const CustomerBetaListPage = lazyPage(() => import('./pages/customers/beta'), 'CustomerBetaListPage');
const ContactPersonDetailPage = lazyPage(
  () => import('./pages/customers/contact-persons/[id]'),
  'ContactPersonDetailPage',
);
const ContactPersonLoginInfoPage = lazyPage(
  () => import('./pages/customers/contact-persons/[id]/edi-account-info'),
  'ContactPersonLoginInfoPage',
);
const ContactPersonImportPage = lazyPage(
  () => import('./pages/customers/contact-persons/import'),
  'ContactPersonImportPage',
);
const ContactPersonListPage = lazyPage(
  () => import('./pages/customers/contact-persons/index'),
  'ContactPersonListPage',
);
const ContactPersonCreatePage = lazyPage(
  () => import('./pages/customers/contact-persons/new'),
  'ContactPersonCreatePage',
);
const CustomerImportPage = lazyPage(() => import('./pages/customers/import'), 'CustomerImportPage');
const CustomerCreatePage = lazyPage(() => import('./pages/customers/new'), 'CustomerCreatePage');
const ExplorePage = lazyPage(() => import('./pages/explore'), 'ExplorePage');
const CustomerExplorePage = lazyPage(() => import('./pages/explore/customers'), 'CustomerExplorePage');
const CustomerExploreDetailPage = lazyPage(
  () => import('./pages/explore/customers/[id]'),
  'CustomerExploreDetailPage',
);
const CustomerProfitExplorePage = lazyPage(
  () => import('./pages/explore/customers/profit'),
  'CustomerProfitExplorePage',
);
const DashboardExplorePage = lazyPage(() => import('./pages/explore/dashboard'), 'DashboardExplorePage');
const ProductExplorePage = lazyPage(() => import('./pages/explore/products'), 'ProductExplorePage');
const ProductExploreDetailPage = lazyPage(
  () => import('./pages/explore/products/[id]'),
  'ProductExploreDetailPage',
);
const ProductProfitExplorePage = lazyPage(
  () => import('./pages/explore/products/profit'),
  'ProductProfitExplorePage',
);
const InsightsPage = lazyPage(() => import('./pages/insights'), 'InsightsPage');
const CustomerInsightsPage = lazyPage(() => import('./pages/insights/customers'), 'CustomerInsightsPage');
const CustomerInsightsDetailPage = lazyPage(
  () => import('./pages/insights/customers/[id]'),
  'CustomerInsightsDetailPage',
);
const CustomerProfitInsightsPage = lazyPage(
  () => import('./pages/insights/customers/profit'),
  'CustomerProfitInsightsPage',
);
const ProductInsightsPage = lazyPage(() => import('./pages/insights/products'), 'ProductInsightsPage');
const ProductInsightsDetailPage = lazyPage(
  () => import('./pages/insights/products/[id]'),
  'ProductInsightsDetailPage',
);
const ProductProfitInsightsPage = lazyPage(
  () => import('./pages/insights/products/profit'),
  'ProductProfitInsightsPage',
);
const PurchaseInsightsPage = lazyPage(() => import('./pages/insights/purchases'), 'PurchaseInsightsPage');
const SupplierInsightsDetailPage = lazyPage(
  () => import('./pages/insights/purchases/[id]'),
  'SupplierInsightsDetailPage',
);
const InventoryListPage = lazyPage(() => import('./pages/inventory/list'), 'InventoryListPage');
const StockAdjustmentDetailPage = lazyPage(
  () => import('./pages/inventory/stock-adjustment'),
  'StockAdjustmentDetailPage',
);
const StockAdjustmentItemListPage = lazyPage(
  () => import('./pages/inventory/stock-adjustment'),
  'StockAdjustmentItemListPage',
);
const StockAdjustmentListPage = lazyPage(
  () => import('./pages/inventory/stock-adjustment'),
  'StockAdjustmentListPage',
);
const StocktakingEditPage = lazyPage(
  () => import('./pages/inventory/stocktaking/edit'),
  'StocktakingEditPage',
);
const StocktakingListPage = lazyPage(
  () => import('./pages/inventory/stocktaking/list'),
  'StocktakingListPage',
);
const StocktakingNewPage = lazyPage(() => import('./pages/inventory/stocktaking/new'), 'StocktakingNewPage');
const LotListPage = lazyPage(() => import('./pages/inventory/lots/list'), 'LotListPage');
const MemberInvitationAcceptPage = lazyPage(
  () => import('./pages/invitation/accept'),
  'MemberInvitationAcceptPage',
);
const LeadDetailPage = lazyPage(() => import('./pages/leads/[id]'), 'LeadDetailPage');
const ResearcherJpDetailPage = lazyPage(
  () => import('./pages/leads/[id]/researcher-jp'),
  'ResearcherJpDetailPage',
);
const LeadBetaListPage = lazyPage(() => import('./pages/leads/beta'), 'LeadBetaListPage');
const LeadImportPage = lazyPage(() => import('./pages/leads/import'), 'LeadImportPage');
const ResearcherJpImportPage = lazyPage(
  () => import('./pages/leads/import-researcher-jp'),
  'ResearcherJpImportPage',
);
const LeadCreatePage = lazyPage(() => import('./pages/leads/new'), 'LeadCreatePage');
const OutreachListEditPage = lazyPage(
  () => import('./pages/leads/outreach-lists/[id]'),
  'OutreachListEditPage',
);
const OutreachListPage = lazyPage(() => import('./pages/leads/outreach-lists/list'), 'OutreachListPage');
const ManufacturingOrderEditPage = lazyPage(
  () => import('./pages/manufacturing-orders/edit'),
  'ManufacturingOrderEditPage',
);
const ManufacturingOrderListPage = lazyPage(
  () => import('./pages/manufacturing-orders/list'),
  'ManufacturingOrderListPage',
);
const ManufacturingOrderCreatePage = lazyPage(
  () => import('./pages/manufacturing-orders/new'),
  'ManufacturingOrderCreatePage',
);
const AccountsReceivableDetailPage = lazyPage(
  () => import('./pages/orders/accounts-receivable/detail'),
  'AccountsReceivableDetailPage',
);
const AccountsReceivableListPage = lazyPage(
  () => import('./pages/orders/accounts-receivable/list'),
  'AccountsReceivableListPage',
);
const ClosingListPage = lazyPage(() => import('./pages/orders/closings'), 'ClosingListPage');
const ClosingDetailPage = lazyPage(() => import('./pages/orders/closings/detail'), 'ClosingDetailPage');
const ClosingNewPage = lazyPage(() => import('./pages/orders/closings/new'), 'ClosingNewPage');
const DeliveryNoteEmailPage = lazyPage(
  () => import('./pages/orders/delivery-note-email'),
  'DeliveryNoteEmailPage',
);
const EdiPurchaseOrderListPage = lazyPage(
  () => import('./pages/orders/edi-purchase-orders/list'),
  'EdiPurchaseOrderListPage',
);
const OrderEditPage = lazyPage(() => import('./pages/orders/edit'), 'OrderEditPage');
const OrderEmailPage = lazyPage(() => import('./pages/orders/email'), 'OrderEmailPage');
const SalesOrderImportPage = lazyPage(() => import('./pages/orders/import'), 'SalesOrderImportPage');
const IncomingPaymentAllocationsPage = lazyPage(
  () => import('./pages/orders/incoming-payments/allocations'),
  'IncomingPaymentAllocationsPage',
);
const IncomingPaymentEditPage = lazyPage(
  () => import('./pages/orders/incoming-payments/edit'),
  'IncomingPaymentEditPage',
);
const IncomingPaymentFromInvoicesPage = lazyPage(
  () => import('./pages/orders/incoming-payments/from-invoices'),
  'IncomingPaymentFromInvoicesPage',
);
const IncomingPaymentFromPaymentsPage = lazyPage(
  () => import('./pages/orders/incoming-payments/from-payments'),
  'IncomingPaymentFromPaymentsPage',
);
const IncomingPaymentImportPage = lazyPage(
  () => import('./pages/orders/incoming-payments/import'),
  'IncomingPaymentImportPage',
);
const IncomingPaymentListPage = lazyPage(
  () => import('./pages/orders/incoming-payments/list'),
  'IncomingPaymentListPage',
);
const InvoiceDetailPage = lazyPage(() => import('./pages/orders/invoices'), 'InvoiceDetailPage');
const InvoiceListPage = lazyPage(() => import('./pages/orders/invoices'), 'InvoiceListPage');
const ConsolidateOrdersInvoicePage = lazyPage(
  () => import('./pages/orders/invoices/consolidate'),
  'ConsolidateOrdersInvoicePage',
);
const InvoiceEmailPage = lazyPage(() => import('./pages/orders/invoices/email'), 'InvoiceEmailPage');
const InvoiceImportPage = lazyPage(() => import('./pages/orders/invoices/import'), 'InvoiceImportPage');
const OrderItemListPage = lazyPage(() => import('./pages/orders/items'), 'OrderItemListPage');
const OrderItemsStatsPage = lazyPage(() => import('./pages/orders/items-stats'), 'OrderItemsStatsPage');
const OrderListPage = lazyPage(() => import('./pages/orders/list'), 'OrderListPage');
const OrdersCalendarPage = lazyPage(() => import('./pages/orders/list-calendar'), 'OrdersCalendarPage');
const QuotationEditPage = lazyPage(() => import('./pages/orders/quotations/edit'), 'QuotationEditPage');
const QuotationEmailPage = lazyPage(() => import('./pages/orders/quotations/email'), 'QuotationEmailPage');
const QuotationImportPage = lazyPage(() => import('./pages/orders/quotations/import'), 'QuotationImportPage');
const QuotationListPage = lazyPage(() => import('./pages/orders/quotations/list'), 'QuotationListPage');
const SalesItemListPage = lazyPage(() => import('./pages/orders/sales/items'), 'SalesItemListPage');
const SalesItemsStatsPage = lazyPage(() => import('./pages/orders/sales/items-stats'), 'SalesItemsStatsPage');
const SalesListPage = lazyPage(() => import('./pages/orders/sales/list'), 'SalesListPage');
const ShipmentScheduleListPage = lazyPage(
  () => import('./pages/orders/shipment-schedule'),
  'ShipmentScheduleListPage',
);
const ProductListPage = lazyPage(() => import('./pages/products'), 'ProductListPage');
const ProductDetailPage = lazyPage(() => import('./pages/products/[id]'), 'ProductDetailPage');
const ProductCatalogPage = lazyPage(
  () => import('./pages/products/[id]/catalog/index'),
  'ProductCatalogPage',
);
const ProductCustomerModelNumbersPage = lazyPage(
  () => import('./pages/products/[id]/customer-model-numbers'),
  'ProductCustomerModelNumbersPage',
);
const ProductDocumentsPage = lazyPage(
  () => import('./pages/products/[id]/documents'),
  'ProductDocumentsPage',
);
const ProductIntegrationsPage = lazyPage(
  () => import('./pages/products/[id]/integrations'),
  'ProductIntegrationsPage',
);
const ProductVariationPartnerPriceGroupSettingsPage = lazyPage(
  () => import('./pages/products/[id]/partner-price-group/[priceGroupId]'),
  'ProductVariationPartnerPriceGroupSettingsPage',
);
const ProductPartsPage = lazyPage(() => import('./pages/products/[id]/parts'), 'ProductPartsPage');
const ProductPricePage = lazyPage(() => import('./pages/products/[id]/price'), 'ProductPricePage');
const ProductPriceChangeLogsPage = lazyPage(
  () => import('./pages/products/[id]/price-change-logs'),
  'ProductPriceChangeLogsPage',
);
const ProductSuppliersPage = lazyPage(
  () => import('./pages/products/[id]/suppliers'),
  'ProductSuppliersPage',
);
const PartnerCatalogListPage = lazyPage(() => import('./pages/products/catalogs'), 'PartnerCatalogListPage');
const PartnerCatalogDetailPage = lazyPage(
  () => import('./pages/products/catalogs/[id]'),
  'PartnerCatalogDetailPage',
);
const PartnerCatalogPartnersPage = lazyPage(
  () => import('./pages/products/catalogs/[id]/partners'),
  'PartnerCatalogPartnersPage',
);
const PartnerCatalogCreatePage = lazyPage(
  () => import('./pages/products/catalogs/new'),
  'PartnerCatalogCreatePage',
);
const CustomerPriceListPage = lazyPage(
  () => import('./pages/products/customer-prices'),
  'CustomerPriceListPage',
);
const CustomerPriceImportPage = lazyPage(
  () => import('./pages/products/customer-prices/import'),
  'CustomerPriceImportPage',
);
const ProductImportPage = lazyPage(() => import('./pages/products/import'), 'ProductImportPage');
const ProductCreatePage = lazyPage(() => import('./pages/products/new'), 'ProductCreatePage');
const PriceRevisionEditPage = lazyPage(
  () => import('./pages/products/price-revisions/edit'),
  'PriceRevisionEditPage',
);
const PriceRevisionListPage = lazyPage(
  () => import('./pages/products/price-revisions/list'),
  'PriceRevisionListPage',
);
const PriceRevisionNewPage = lazyPage(
  () => import('./pages/products/price-revisions/new'),
  'PriceRevisionNewPage',
);
const ProductPresetEditPage = lazyPage(
  () => import('./pages/products/presets/edit'),
  'ProductPresetEditPage',
);
const ProductPresetListPage = lazyPage(
  () => import('./pages/products/presets/list'),
  'ProductPresetListPage',
);
const ProductPresetNewPage = lazyPage(() => import('./pages/products/presets/new'), 'ProductPresetNewPage');
const QuantityPriceListPage = lazyPage(
  () => import('./pages/products/quantity-prices'),
  'QuantityPriceListPage',
);
const QuantityPriceImportPage = lazyPage(
  () => import('./pages/products/quantity-prices/import'),
  'QuantityPriceImportPage',
);
const ProjectCostDetailPage = lazyPage(() => import('./pages/projects'), 'ProjectCostDetailPage');
const ProjectCostListPage = lazyPage(() => import('./pages/projects'), 'ProjectCostListPage');
const ProjectDetailPage = lazyPage(() => import('./pages/projects'), 'ProjectDetailPage');
const ProjectDocumentsPage = lazyPage(() => import('./pages/projects'), 'ProjectDocumentsPage');
const ProjectGanttPage = lazyPage(() => import('./pages/projects'), 'ProjectGanttPage');
const ProjectListPage = lazyPage(() => import('./pages/projects'), 'ProjectListPage');
const ProjectNewPage = lazyPage(() => import('./pages/projects'), 'ProjectNewPage');
const AccountsPayableByCategoryPage = lazyPage(
  () => import('./pages/purchase-orders/accounts-payable/by-category'),
  'AccountsPayableByCategoryPage',
);
const AccountsPayableDetailPage = lazyPage(
  () => import('./pages/purchase-orders/accounts-payable/detail'),
  'AccountsPayableDetailPage',
);
const AccountsPayableListPage = lazyPage(
  () => import('./pages/purchase-orders/accounts-payable/list'),
  'AccountsPayableListPage',
);
const ArrivalsListPage = lazyPage(() => import('./pages/purchase-orders/arrivals'), 'ArrivalsListPage');
const PurchaseClosingListPage = lazyPage(
  () => import('./pages/purchase-orders/closings'),
  'PurchaseClosingListPage',
);
const PurchaseClosingDetailPage = lazyPage(
  () => import('./pages/purchase-orders/closings/detail'),
  'PurchaseClosingDetailPage',
);
const PurchaseClosingNewPage = lazyPage(
  () => import('./pages/purchase-orders/closings/new'),
  'PurchaseClosingNewPage',
);
const PurchaseOrderEditPage = lazyPage(() => import('./pages/purchase-orders/edit'), 'PurchaseOrderEditPage');
const PurchaseOrderEmailPage = lazyPage(
  () => import('./pages/purchase-orders/email'),
  'PurchaseOrderEmailPage',
);
const PurchaseOrderFaxPage = lazyPage(() => import('./pages/purchase-orders/fax'), 'PurchaseOrderFaxPage');
const PurchaseOrderImportPage = lazyPage(
  () => import('./pages/purchase-orders/import'),
  'PurchaseOrderImportPage',
);
const PurchaseOrderItemListPage = lazyPage(
  () => import('./pages/purchase-orders/items'),
  'PurchaseOrderItemListPage',
);
const PurchaseOrderListPage = lazyPage(() => import('./pages/purchase-orders/list'), 'PurchaseOrderListPage');
const OutgoingPaymentDetailPage = lazyPage(
  () => import('./pages/purchase-orders/outgoing-payments'),
  'OutgoingPaymentDetailPage',
);
const OutgoingPaymentImportPage = lazyPage(
  () => import('./pages/purchase-orders/outgoing-payments'),
  'OutgoingPaymentImportPage',
);
const OutgoingPaymentListPage = lazyPage(
  () => import('./pages/purchase-orders/outgoing-payments'),
  'OutgoingPaymentListPage',
);
const PurchaseInvoiceDetailPage = lazyPage(
  () => import('./pages/purchase-orders/purchase-invoices'),
  'PurchaseInvoiceDetailPage',
);
const PurchaseInvoiceImportPage = lazyPage(
  () => import('./pages/purchase-orders/purchase-invoices'),
  'PurchaseInvoiceImportPage',
);
const PurchaseInvoiceListPage = lazyPage(
  () => import('./pages/purchase-orders/purchase-invoices'),
  'PurchaseInvoiceListPage',
);
const ConsolidatePurchaseOrdersInvoicePage = lazyPage(
  () => import('./pages/purchase-orders/purchase-invoices/consolidate'),
  'ConsolidatePurchaseOrdersInvoicePage',
);
const PurchaseItemListPage = lazyPage(
  () => import('./pages/purchase-orders/purchases/items'),
  'PurchaseItemListPage',
);
const PurchaseListPage = lazyPage(() => import('./pages/purchase-orders/purchases/list'), 'PurchaseListPage');
const NoticeListPage = lazyPage(() => import('./pages/notices/list'), 'NoticeListPage');
const ResourceLinkRedirectPage = lazyPage(
  () => import('./pages/resource-links/redirect'),
  'ResourceLinkRedirectPage',
);
const ApiSettingsPage = lazyPage(() => import('./pages/settings/api'), 'ApiSettingsPage');
const DocumentSettingsPage = lazyPage(
  () => import('./pages/settings/document-settings'),
  'DocumentSettingsPage',
);
const DocumentLogoSettingsPage = lazyPage(
  () => import('./pages/settings/document-settings/logo'),
  'DocumentLogoSettingsPage',
);
const NotesTemplateEditPage = lazyPage(
  () => import('./pages/settings/document-settings/notes-templates'),
  'NotesTemplateEditPage',
);
const NotesTemplateNewPage = lazyPage(
  () => import('./pages/settings/document-settings/notes-templates'),
  'NotesTemplateNewPage',
);
const NotesTemplatesListPage = lazyPage(
  () => import('./pages/settings/document-settings/notes-templates'),
  'NotesTemplatesListPage',
);
const EdiDisplaySettingsPage = lazyPage(
  () => import('./pages/settings/edi/display'),
  'EdiDisplaySettingsPage',
);
const EdiNotificationSettingsPage = lazyPage(
  () => import('./pages/settings/edi/notifications'),
  'EdiNotificationSettingsPage',
);
const EdiNoticeCreatePage = lazyPage(() => import('./pages/settings/edi/notices'), 'EdiNoticeCreatePage');
const EdiNoticeEditPage = lazyPage(() => import('./pages/settings/edi/notices'), 'EdiNoticeEditPage');
const EdiNoticeListPage = lazyPage(() => import('./pages/settings/edi/notices'), 'EdiNoticeListPage');
const ExternalIntegrationsSettingsPage = lazyPage(
  () => import('./pages/settings/external-integrations'),
  'ExternalIntegrationsSettingsPage',
);
const ExternalIntegrationsEditPage = lazyPage(
  () => import('./pages/settings/external-integrations/edit'),
  'ExternalIntegrationsEditPage',
);
const ExternalIntegrationsNewPage = lazyPage(
  () => import('./pages/settings/external-integrations/new'),
  'ExternalIntegrationsNewPage',
);
const InventorySettingsPage = lazyPage(() => import('./pages/settings/inventory'), 'InventorySettingsPage');
const StockAdjustmentCustomPropertyCreatePage = lazyPage(
  () => import('./pages/settings/inventory/custom-properties'),
  'StockAdjustmentCustomPropertyCreatePage',
);
const StockAdjustmentCustomPropertyEditPage = lazyPage(
  () => import('./pages/settings/inventory/custom-properties'),
  'StockAdjustmentCustomPropertyEditPage',
);
const StockAdjustmentCustomPropertyListPage = lazyPage(
  () => import('./pages/settings/inventory/custom-properties'),
  'StockAdjustmentCustomPropertyListPage',
);
const InvoiceSettingsPage = lazyPage(() => import('./pages/settings/invoices'), 'InvoiceSettingsPage');
const BankAccountEditPage = lazyPage(
  () => import('./pages/settings/invoices/bank-accounts/edit'),
  'BankAccountEditPage',
);
const BankAccountCreatePage = lazyPage(
  () => import('./pages/settings/invoices/bank-accounts/new'),
  'BankAccountCreatePage',
);
const InvoiceEmailTemplateSettingPage = lazyPage(
  () => import('./pages/settings/invoices/email-template'),
  'InvoiceEmailTemplateSettingPage',
);
const InvoiceExportSettingsEdit = lazyPage(
  () => import('./pages/settings/invoices/export-settings/edit'),
  'InvoiceExportSettingsEdit',
);
const InvoiceExportSettingsListPage = lazyPage(
  () => import('./pages/settings/invoices/export-settings/index'),
  'InvoiceExportSettingsListPage',
);
const InvoiceExportSettingsNew = lazyPage(
  () => import('./pages/settings/invoices/export-settings/new'),
  'InvoiceExportSettingsNew',
);
const InvoiceTagSettingsPage = lazyPage(
  () => import('./pages/settings/invoices/invoice-tags'),
  'InvoiceTagSettingsPage',
);
const InvoiceTagEditPage = lazyPage(
  () => import('./pages/settings/invoices/invoice-tags/edit'),
  'InvoiceTagEditPage',
);
const InvoiceTagCreatePage = lazyPage(
  () => import('./pages/settings/invoices/invoice-tags/new'),
  'InvoiceTagCreatePage',
);
const InvoiceTemplateSettingPage = lazyPage(
  () => import('./pages/settings/invoices/invoice-template'),
  'InvoiceTemplateSettingPage',
);
const PaymentMethodListPage = lazyPage(
  () => import('./pages/settings/invoices/payment-methods'),
  'PaymentMethodListPage',
);
const PaymentMethodEditPage = lazyPage(
  () => import('./pages/settings/invoices/payment-methods/edit'),
  'PaymentMethodEditPage',
);
const PaymentMethodNewPage = lazyPage(
  () => import('./pages/settings/invoices/payment-methods/new'),
  'PaymentMethodNewPage',
);
const ItemDisplaySettingsPage = lazyPage(
  () => import('./pages/settings/item-display-settings/index'),
  'ItemDisplaySettingsPage',
);
const LeadSourceEditPage = lazyPage(
  () => import('./pages/settings/lead-source-settings'),
  'LeadSourceEditPage',
);
const LeadSourceNewPage = lazyPage(
  () => import('./pages/settings/lead-source-settings'),
  'LeadSourceNewPage',
);
const LeadSourceSettingsPage = lazyPage(
  () => import('./pages/settings/lead-source-settings'),
  'LeadSourceSettingsPage',
);
const CompanyNamePatternCreatePage = lazyPage(
  () => import('./pages/settings/leads/company-name-patterns'),
  'CompanyNamePatternCreatePage',
);
const CompanyNamePatternEditPage = lazyPage(
  () => import('./pages/settings/leads/company-name-patterns'),
  'CompanyNamePatternEditPage',
);
const CompanyNamePatternListPage = lazyPage(
  () => import('./pages/settings/leads/company-name-patterns'),
  'CompanyNamePatternListPage',
);
const LeadCustomPropertyCreatePage = lazyPage(
  () => import('./pages/settings/leads/custom-properties'),
  'LeadCustomPropertyCreatePage',
);
const LeadCustomPropertyEditPage = lazyPage(
  () => import('./pages/settings/leads/custom-properties'),
  'LeadCustomPropertyEditPage',
);
const LeadCustomPropertyListPage = lazyPage(
  () => import('./pages/settings/leads/custom-properties'),
  'LeadCustomPropertyListPage',
);
const LeadTagCreatePage = lazyPage(() => import('./pages/settings/leads/tags'), 'LeadTagCreatePage');
const LeadTagEditPage = lazyPage(() => import('./pages/settings/leads/tags'), 'LeadTagEditPage');
const LeadTagImportPage = lazyPage(() => import('./pages/settings/leads/tags'), 'LeadTagImportPage');
const LeadTagSettingsPage = lazyPage(() => import('./pages/settings/leads/tags'), 'LeadTagSettingsPage');
const LocationSettingsPage = lazyPage(() => import('./pages/settings/locations'), 'LocationSettingsPage');
const ShelfMasterEditPage = lazyPage(
  () => import('./pages/settings/locations/shelf-master-edit'),
  'ShelfMasterEditPage',
);
const ShelfMasterNewPage = lazyPage(
  () => import('./pages/settings/locations/shelf-master-new'),
  'ShelfMasterNewPage',
);
const WarehouseEditPage = lazyPage(
  () => import('./pages/settings/locations/warehouses/[id]/edit'),
  'WarehouseEditPage',
);
const WarehouseNewPage = lazyPage(
  () => import('./pages/settings/locations/warehouses/new'),
  'WarehouseNewPage',
);
const ManufacturingOrderCustomPropertyCreatePage = lazyPage(
  () => import('./pages/settings/manufacturing-orders/custom-properties'),
  'ManufacturingOrderCustomPropertyCreatePage',
);
const ManufacturingOrderCustomPropertyEditPage = lazyPage(
  () => import('./pages/settings/manufacturing-orders/custom-properties'),
  'ManufacturingOrderCustomPropertyEditPage',
);
const ManufacturingOrderCustomPropertyListPage = lazyPage(
  () => import('./pages/settings/manufacturing-orders/custom-properties'),
  'ManufacturingOrderCustomPropertyListPage',
);
const MemberListPage = lazyPage(() => import('./pages/settings/members'), 'MemberListPage');
const DepartmentCreatePage = lazyPage(
  () => import('./pages/settings/members/departments/index'),
  'DepartmentCreatePage',
);
const DepartmentEditPage = lazyPage(
  () => import('./pages/settings/members/departments/index'),
  'DepartmentEditPage',
);
const DepartmentListPage = lazyPage(
  () => import('./pages/settings/members/departments/index'),
  'DepartmentListPage',
);
const MemberEditPage = lazyPage(() => import('./pages/settings/members/edit'), 'MemberEditPage');
const OrderAccuracyEditPage = lazyPage(
  () => import('./pages/settings/order-accuracy-settings'),
  'OrderAccuracyEditPage',
);
const OrderAccuracyNewPage = lazyPage(
  () => import('./pages/settings/order-accuracy-settings'),
  'OrderAccuracyNewPage',
);
const OrderAccuracySettingsPage = lazyPage(
  () => import('./pages/settings/order-accuracy-settings'),
  'OrderAccuracySettingsPage',
);
const OrganizationBasicInfoPage = lazyPage(
  () => import('./pages/settings/organization'),
  'OrganizationBasicInfoPage',
);
const HeaderMenuSettingsPage = lazyPage(
  () => import('./pages/settings/organization/header-menus'),
  'HeaderMenuSettingsPage',
);
const OrganizationSettingsLayout = lazyPage(
  () => import('./pages/settings/organization/layout'),
  'OrganizationSettingsLayout',
);
const ProfitMarginPage = lazyPage(
  () => import('./pages/settings/organization/profit-margin'),
  'ProfitMarginPage',
);
const PartnerActivityCustomPropertyCreatePage = lazyPage(
  () => import('./pages/settings/partners/activity-custom-properties'),
  'PartnerActivityCustomPropertyCreatePage',
);
const PartnerActivityCustomPropertyEditPage = lazyPage(
  () => import('./pages/settings/partners/activity-custom-properties'),
  'PartnerActivityCustomPropertyEditPage',
);
const PartnerActivityCustomPropertyListPage = lazyPage(
  () => import('./pages/settings/partners/activity-custom-properties'),
  'PartnerActivityCustomPropertyListPage',
);
const PartnerActivityTypeCreatePage = lazyPage(
  () => import('./pages/settings/partners/activity-types'),
  'PartnerActivityTypeCreatePage',
);
const PartnerActivityTypeEditPage = lazyPage(
  () => import('./pages/settings/partners/activity-types'),
  'PartnerActivityTypeEditPage',
);
const PartnerActivityTypeSettingsPage = lazyPage(
  () => import('./pages/settings/partners/activity-types'),
  'PartnerActivityTypeSettingsPage',
);
const OrganizationAmountCalculationSettingsPage = lazyPage(
  () => import('./pages/settings/partners/amount-calculation'),
  'OrganizationAmountCalculationSettingsPage',
);
const PartnerCategoryCreatePage = lazyPage(
  () => import('./pages/settings/partners/categories'),
  'PartnerCategoryCreatePage',
);
const PartnerCategoryEditPage = lazyPage(
  () => import('./pages/settings/partners/categories'),
  'PartnerCategoryEditPage',
);
const PartnerCategoryListPage = lazyPage(
  () => import('./pages/settings/partners/categories'),
  'PartnerCategoryListPage',
);
const ClosingGroupCreatePage = lazyPage(
  () => import('./pages/settings/partners/closing-groups'),
  'ClosingGroupCreatePage',
);
const ClosingGroupEditPage = lazyPage(
  () => import('./pages/settings/partners/closing-groups'),
  'ClosingGroupEditPage',
);
const ClosingGroupListPage = lazyPage(
  () => import('./pages/settings/partners/closing-groups'),
  'ClosingGroupListPage',
);
const PartnerCustomPropertyCreatePage = lazyPage(
  () => import('./pages/settings/partners/custom-properties'),
  'PartnerCustomPropertyCreatePage',
);
const PartnerCustomPropertyEditPage = lazyPage(
  () => import('./pages/settings/partners/custom-properties'),
  'PartnerCustomPropertyEditPage',
);
const PartnerCustomPropertyImportPage = lazyPage(
  () => import('./pages/settings/partners/custom-properties'),
  'PartnerCustomPropertyImportPage',
);
const PartnerCustomPropertyListPage = lazyPage(
  () => import('./pages/settings/partners/custom-properties'),
  'PartnerCustomPropertyListPage',
);
const PartnerCustomPropertyGroupCreatePage = lazyPage(
  () => import('./pages/settings/partners/custom-properties/groups'),
  'PartnerCustomPropertyGroupCreatePage',
);
const PartnerCustomPropertyGroupEditPage = lazyPage(
  () => import('./pages/settings/partners/custom-properties/groups'),
  'PartnerCustomPropertyGroupEditPage',
);
const PartnerCustomPropertyGroupListPage = lazyPage(
  () => import('./pages/settings/partners/custom-properties/groups'),
  'PartnerCustomPropertyGroupListPage',
);
const DeliveryDestinationLabelCreatePage = lazyPage(
  () => import('./pages/settings/partners/delivery-destination-labels'),
  'DeliveryDestinationLabelCreatePage',
);
const DeliveryDestinationLabelEditPage = lazyPage(
  () => import('./pages/settings/partners/delivery-destination-labels'),
  'DeliveryDestinationLabelEditPage',
);
const DeliveryDestinationLabelSettingsPage = lazyPage(
  () => import('./pages/settings/partners/delivery-destination-labels'),
  'DeliveryDestinationLabelSettingsPage',
);
const PartnerExportSettingsEdit = lazyPage(
  () => import('./pages/settings/partners/export-settings/edit'),
  'PartnerExportSettingsEdit',
);
const PartnerExportSettingsListPage = lazyPage(
  () => import('./pages/settings/partners/export-settings/index'),
  'PartnerExportSettingsListPage',
);
const PartnerExportSettingsNew = lazyPage(
  () => import('./pages/settings/partners/export-settings/new'),
  'PartnerExportSettingsNew',
);
const PartnerTagCreatePage = lazyPage(
  () => import('./pages/settings/partners/partner-tags'),
  'PartnerTagCreatePage',
);
const PartnerTagEditPage = lazyPage(
  () => import('./pages/settings/partners/partner-tags'),
  'PartnerTagEditPage',
);
const PartnerTagSettingsPage = lazyPage(
  () => import('./pages/settings/partners/partner-tags'),
  'PartnerTagSettingsPage',
);
const PartnerPriceGroupSettingsPage = lazyPage(
  () => import('./pages/settings/partners/price-groups'),
  'PartnerPriceGroupSettingsPage',
);
const AccountTitleListPage = lazyPage(
  () => import('./pages/settings/payments/account-titles'),
  'AccountTitleListPage',
);
const AccountTitleEditPage = lazyPage(
  () => import('./pages/settings/payments/account-titles/edit'),
  'AccountTitleEditPage',
);
const AccountTitleNewPage = lazyPage(
  () => import('./pages/settings/payments/account-titles/new'),
  'AccountTitleNewPage',
);
const UnitListPage = lazyPage(() => import('./pages/settings/products'), 'UnitListPage');
const ProductCategoryCreatePage = lazyPage(
  () => import('./pages/settings/products/categories/index'),
  'ProductCategoryCreatePage',
);
const ProductCategoryEditPage = lazyPage(
  () => import('./pages/settings/products/categories/index'),
  'ProductCategoryEditPage',
);
const ProductCategoryListPage = lazyPage(
  () => import('./pages/settings/products/categories/index'),
  'ProductCategoryListPage',
);
const CatalogCustomPropertyGroupCreatePage = lazyPage(
  () => import('./pages/settings/products/custom-properties/groups/index'),
  'CatalogCustomPropertyGroupCreatePage',
);
const CatalogCustomPropertyGroupEditPage = lazyPage(
  () => import('./pages/settings/products/custom-properties/groups/index'),
  'CatalogCustomPropertyGroupEditPage',
);
const CatalogCustomPropertyGroupListPage = lazyPage(
  () => import('./pages/settings/products/custom-properties/groups/index'),
  'CatalogCustomPropertyGroupListPage',
);
const CatalogCustomPropertyCreatePage = lazyPage(
  () => import('./pages/settings/products/custom-properties/index'),
  'CatalogCustomPropertyCreatePage',
);
const CatalogCustomPropertyEditPage = lazyPage(
  () => import('./pages/settings/products/custom-properties/index'),
  'CatalogCustomPropertyEditPage',
);
const CatalogCustomPropertyImportPage = lazyPage(
  () => import('./pages/settings/products/custom-properties/index'),
  'CatalogCustomPropertyImportPage',
);
const CatalogCustomPropertyListPage = lazyPage(
  () => import('./pages/settings/products/custom-properties/index'),
  'CatalogCustomPropertyListPage',
);
const PriceRevisionCustomPropertyCreatePage = lazyPage(
  () => import('./pages/settings/products/price-revision-custom-properties'),
  'PriceRevisionCustomPropertyCreatePage',
);
const PriceRevisionCustomPropertyEditPage = lazyPage(
  () => import('./pages/settings/products/price-revision-custom-properties'),
  'PriceRevisionCustomPropertyEditPage',
);
const PriceRevisionCustomPropertyListPage = lazyPage(
  () => import('./pages/settings/products/price-revision-custom-properties'),
  'PriceRevisionCustomPropertyListPage',
);
const UnitEditPage = lazyPage(() => import('./pages/settings/products/edit'), 'UnitEditPage');
const UnitCreatePage = lazyPage(() => import('./pages/settings/products/new'), 'UnitCreatePage');
const PackageTypeListPage = lazyPage(
  () => import('./pages/settings/products/package-types'),
  'PackageTypeListPage',
);
const PackageTypeEditPage = lazyPage(
  () => import('./pages/settings/products/package-types/[id]/edit'),
  'PackageTypeEditPage',
);
const PackageTypeCreatePage = lazyPage(
  () => import('./pages/settings/products/package-types/new'),
  'PackageTypeCreatePage',
);
const ProjectCustomPropertyCreatePage = lazyPage(
  () => import('./pages/settings/projects'),
  'ProjectCustomPropertyCreatePage',
);
const ProjectCustomPropertyEditPage = lazyPage(
  () => import('./pages/settings/projects'),
  'ProjectCustomPropertyEditPage',
);
const ProjectCustomPropertyListPage = lazyPage(
  () => import('./pages/settings/projects'),
  'ProjectCustomPropertyListPage',
);
const ProjectStatusEditPage = lazyPage(() => import('./pages/settings/projects'), 'ProjectStatusEditPage');
const ProjectStatusNewPage = lazyPage(() => import('./pages/settings/projects'), 'ProjectStatusNewPage');
const ProjectStatusSettingsPage = lazyPage(
  () => import('./pages/settings/projects'),
  'ProjectStatusSettingsPage',
);
const PurchaseOrderCustomPropertyCreatePage = lazyPage(
  () => import('./pages/settings/purchase-orders/custom-properties'),
  'PurchaseOrderCustomPropertyCreatePage',
);
const PurchaseOrderCustomPropertyEditPage = lazyPage(
  () => import('./pages/settings/purchase-orders/custom-properties'),
  'PurchaseOrderCustomPropertyEditPage',
);
const PurchaseOrderCustomPropertyListPage = lazyPage(
  () => import('./pages/settings/purchase-orders/custom-properties'),
  'PurchaseOrderCustomPropertyListPage',
);
const PurchaseOrderEmailTemplateSettingPage = lazyPage(
  () => import('./pages/settings/purchase-orders/email-template'),
  'PurchaseOrderEmailTemplateSettingPage',
);
const PurchaseOrderExportSettingsEdit = lazyPage(
  () => import('./pages/settings/purchase-orders/export-settings/edit'),
  'PurchaseOrderExportSettingsEdit',
);
const PurchaseOrderExportSettingsListPage = lazyPage(
  () => import('./pages/settings/purchase-orders/export-settings/index'),
  'PurchaseOrderExportSettingsListPage',
);
const PurchaseOrderExportSettingsNew = lazyPage(
  () => import('./pages/settings/purchase-orders/export-settings/new'),
  'PurchaseOrderExportSettingsNew',
);
const PurchaseOrderLabelEditPage = lazyPage(
  () => import('./pages/settings/purchase-orders/labels'),
  'PurchaseOrderLabelEditPage',
);
const PurchaseOrderLabelListPage = lazyPage(
  () => import('./pages/settings/purchase-orders/labels'),
  'PurchaseOrderLabelListPage',
);
const PurchaseOrderLabelNewPage = lazyPage(
  () => import('./pages/settings/purchase-orders/labels'),
  'PurchaseOrderLabelNewPage',
);
const PurchaseOrderSettingsPage = lazyPage(
  () => import('./pages/settings/purchase-orders/templates'),
  'PurchaseOrderSettingsPage',
);
const QuotationCustomPropertyCreatePage = lazyPage(
  () => import('./pages/settings/quotations/custom-properties'),
  'QuotationCustomPropertyCreatePage',
);
const QuotationCustomPropertyEditPage = lazyPage(
  () => import('./pages/settings/quotations/custom-properties'),
  'QuotationCustomPropertyEditPage',
);
const QuotationCustomPropertyListPage = lazyPage(
  () => import('./pages/settings/quotations/custom-properties'),
  'QuotationCustomPropertyListPage',
);
const QuotationTemplateSettingPage = lazyPage(
  () => import('./pages/settings/quotations/quotation-template'),
  'QuotationTemplateSettingPage',
);
const SalesOrderCustomPropertyCreatePage = lazyPage(
  () => import('./pages/settings/sales-orders'),
  'SalesOrderCustomPropertyCreatePage',
);
const SalesOrderCustomPropertyEditPage = lazyPage(
  () => import('./pages/settings/sales-orders'),
  'SalesOrderCustomPropertyEditPage',
);
const SalesOrderSettingsPage = lazyPage(
  () => import('./pages/settings/sales-orders'),
  'SalesOrderSettingsPage',
);
const SalesOrderBasicSettingsPage = lazyPage(
  () => import('./pages/settings/sales-orders/basic-settings'),
  'SalesOrderBasicSettingsPage',
);
const DeliveryNoteTemplateSettingPage = lazyPage(
  () => import('./pages/settings/sales-orders/delivery-note-template'),
  'DeliveryNoteTemplateSettingPage',
);
const DeliveryNoteEmailTemplateSettingPage = lazyPage(
  () => import('./pages/settings/sales-orders/email-template'),
  'DeliveryNoteEmailTemplateSettingPage',
);
const OrderExportSettingsEdit = lazyPage(
  () => import('./pages/settings/sales-orders/export-settings/edit'),
  'OrderExportSettingsEdit',
);
const OrderExportSettingsListPage = lazyPage(
  () => import('./pages/settings/sales-orders/export-settings/index'),
  'OrderExportSettingsListPage',
);
const OrderExportSettingsNew = lazyPage(
  () => import('./pages/settings/sales-orders/export-settings/new'),
  'OrderExportSettingsNew',
);
const SalesOrderPriorityEditPage = lazyPage(
  () => import('./pages/settings/sales-orders/priorities'),
  'SalesOrderPriorityEditPage',
);
const SalesOrderPriorityListPage = lazyPage(
  () => import('./pages/settings/sales-orders/priorities'),
  'SalesOrderPriorityListPage',
);
const SalesOrderPriorityNewPage = lazyPage(
  () => import('./pages/settings/sales-orders/priorities'),
  'SalesOrderPriorityNewPage',
);
const SalesOrderTemplateSettingPage = lazyPage(
  () => import('./pages/settings/sales-orders/sales-order-template'),
  'SalesOrderTemplateSettingPage',
);
const AuditLogListPage = lazyPage(() => import('./pages/settings/security/audit-logs'), 'AuditLogListPage');
const PasswordChangePage = lazyPage(() => import('./pages/settings/security/password'), 'PasswordChangePage');
const MfaSettingsPage = lazyPage(() => import('./pages/settings/security/mfa'), 'MfaSettingsPage');
const UserSettingsPage = lazyPage(() => import('./pages/settings/user'), 'UserSettingsPage');
const WarehouseAppSettingsPage = lazyPage(
  () => import('./pages/settings/warehouse-app'),
  'WarehouseAppSettingsPage',
);

// /products/:id/price/partner-price-group へのアクセスを /products/:id/price にリダイレクト
const ProductPartnerPriceGroupRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/products/${id}/price`} replace />;
};

// /orders/:id/shipments, /orders/:id/shipments/:shipmentId へのアクセスを /orders/:id にリダイレクト
const OrderShipmentsRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/orders/${id}`} replace />;
};

const CustomerDocumentTemplatesRedirect = () => {
  const { id } = useParams<{ id: string }>();
  return <Navigate to={`/customers/${id}/document-templates/purchase-order`} replace />;
};

// ログイン後・`/` アクセス時の初期遷移先。表示可能な一番左のメニューへリダイレクトする
const DefaultRouteRedirect = () => {
  const path = useDefaultMenuPath();
  return <Navigate to={path} replace />;
};

const AppRoutes = () => {
  return (
    <Routes>
      <Route element={<GuestLayout />}>
        <Route path="/signin" element={<SignInPage />} />
        <Route path="/register" element={<RegisterPage />} />
        <Route path="/terms" element={<TermsOfServicePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/service-list" element={<ServiceListPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/password/reset" element={<ForgotPasswordPage />} />
        <Route path="/invitation/accept/:invitationKey" element={<MemberInvitationAcceptPage />} />
      </Route>

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <HeaderLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DefaultRouteRedirect />} />
        <Route path="r/:kind/*" element={<ResourceLinkRedirectPage />} />
        <Route path="notices" element={<NoticeListPage />} />
        <Route path="chats" element={<ChatPage />} />
        <Route path="insights">
          <Route index element={<InsightsPage />} />
          <Route path="customers" element={<CustomerInsightsPage />} />
          <Route path="customers/profit" element={<CustomerProfitInsightsPage />} />
          <Route path="customers/:id" element={<CustomerInsightsDetailPage />} />
          <Route path="products" element={<ProductInsightsPage />} />
          <Route path="products/profit" element={<ProductProfitInsightsPage />} />
          <Route path="products/:id" element={<ProductInsightsDetailPage />} />
          <Route path="purchases" element={<PurchaseInsightsPage />} />
          <Route path="purchases/:id" element={<SupplierInsightsDetailPage />} />
        </Route>
        <Route path="explore">
          <Route index element={<ExplorePage />} />
          <Route path="customers" element={<CustomerExplorePage />} />
          <Route path="customers/profit" element={<CustomerProfitExplorePage />} />
          <Route path="customers/:id" element={<CustomerExploreDetailPage />} />
          <Route path="products" element={<ProductExplorePage />} />
          <Route path="products/profit" element={<ProductProfitExplorePage />} />
          <Route path="products/:id" element={<ProductExploreDetailPage />} />
          <Route path="dashboard" element={<DashboardExplorePage />} />
        </Route>
        <Route path="products">
          <Route path="price-revisions">
            <Route index element={<PriceRevisionListPage />} />
            <Route path="new" element={<PriceRevisionNewPage />} />
            <Route path=":id" element={<PriceRevisionEditPage />} />
          </Route>
          <Route path="presets">
            <Route index element={<ProductPresetListPage />} />
            <Route path="new" element={<ProductPresetNewPage />} />
            <Route path=":id" element={<ProductPresetEditPage />} />
          </Route>
          <Route index element={<ProductListPage />} />
          {/* 商品一覧(β)は商品一覧へ統合済み。β時代のURLをブックマークしている場合に備えてリダイレクトする */}
          <Route path="beta" element={<Navigate to="/products" replace />} />
          <Route path="new" element={<ProductCreatePage />} />
          <Route path="import" element={<ProductImportPage />} />
          <Route path=":id" element={<ProductDetailPage />} />
          <Route path=":id/price" element={<ProductPricePage />} />
          <Route path=":id/customer-model-numbers" element={<ProductCustomerModelNumbersPage />} />
          <Route path=":id/price-change-logs" element={<ProductPriceChangeLogsPage />} />
          <Route path=":id/price/partner-price-group" element={<ProductPartnerPriceGroupRedirect />} />
          <Route
            path=":id/price/partner-price-group/:priceGroupId"
            element={<ProductVariationPartnerPriceGroupSettingsPage />}
          />
          <Route path=":id/suppliers" element={<ProductSuppliersPage />} />
          <Route path=":id/documents" element={<ProductDocumentsPage />} />
          <Route path=":id/catalog" element={<ProductCatalogPage />} />
          <Route path=":id/parts" element={<ProductPartsPage />} />
          <Route path=":id/integrations" element={<ProductIntegrationsPage />} />
          <Route path="customer-prices" element={<CustomerPriceListPage />} />
          <Route path="customer-prices/import" element={<CustomerPriceImportPage />} />
          <Route path="catalogs" element={<PartnerCatalogListPage />} />
          <Route path="catalogs/new" element={<PartnerCatalogCreatePage />} />
          <Route path="catalogs/:id" element={<PartnerCatalogDetailPage />} />
          <Route path="catalogs/:id/partners" element={<PartnerCatalogPartnersPage />} />
          <Route path="quantity-prices" element={<QuantityPriceListPage />} />
          <Route path="quantity-prices/import" element={<QuantityPriceImportPage />} />
        </Route>
        <Route path="projects">
          <Route index element={<ProjectListPage />} />
          <Route path="gantt" element={<ProjectGanttPage />} />
          <Route path="new" element={<ProjectNewPage />} />
          <Route path=":id" element={<ProjectDetailPage />} />
          <Route path=":id/documents" element={<ProjectDocumentsPage />} />
          <Route path=":id/costs" element={<ProjectCostDetailPage />} />
          <Route path="costs" element={<ProjectCostListPage />} />
        </Route>
        <Route path="leads">
          <Route index element={<LeadBetaListPage />} />
          <Route path="new" element={<LeadCreatePage />} />
          <Route path="import" element={<LeadImportPage />} />
          <Route path=":id" element={<LeadDetailPage />} />
          <Route path=":id/researcher-jp" element={<ResearcherJpDetailPage />} />
          <Route path="import-researcher-jp" element={<ResearcherJpImportPage />} />
          <Route path="outreach-lists">
            <Route index element={<OutreachListPage />} />
            <Route path=":id" element={<OutreachListEditPage />} />
            <Route path=":id/settings" element={<OutreachListEditPage />} />
          </Route>
        </Route>
        <Route path="orders">
          <Route index element={<OrderListPage />} />
          <Route path="calendar" element={<OrdersCalendarPage />} />
          <Route path="items" element={<OrderItemListPage />} />
          <Route path="items-stats" element={<OrderItemsStatsPage />} />
          <Route path="sales" element={<SalesListPage />} />
          <Route path="sales/items" element={<SalesItemListPage />} />
          <Route path="sales/items-stats" element={<SalesItemsStatsPage />} />
          <Route path="shipment-schedule" element={<ShipmentScheduleListPage />} />
          <Route path="import" element={<SalesOrderImportPage />} />
          <Route path="quotations">
            <Route index element={<QuotationListPage />} />
            <Route path="import" element={<QuotationImportPage />} />
            <Route path=":id" element={<QuotationEditPage />} />
            <Route path=":id/email" element={<QuotationEmailPage />} />
          </Route>
          <Route path="edi-purchase-orders" element={<EdiPurchaseOrderListPage />} />
          <Route path="invoices">
            <Route index element={<InvoiceListPage />} />
            <Route path="consolidate" element={<ConsolidateOrdersInvoicePage />} />
            <Route path="import" element={<InvoiceImportPage />} />
            <Route path=":id" element={<InvoiceDetailPage />} />
            <Route path=":id/email" element={<InvoiceEmailPage />} />
          </Route>
          <Route path="accounts-receivable">
            <Route index element={<AccountsReceivableListPage />} />
            <Route path=":id" element={<AccountsReceivableDetailPage />} />
          </Route>
          <Route path="incoming-payments">
            <Route index element={<IncomingPaymentListPage />} />
            <Route path="import" element={<IncomingPaymentImportPage />} />
            <Route path="from-payments" element={<IncomingPaymentFromPaymentsPage />} />
            <Route path="from-invoices" element={<IncomingPaymentFromInvoicesPage />} />
            <Route path=":id" element={<IncomingPaymentEditPage />} />
            <Route path=":id/allocations" element={<IncomingPaymentAllocationsPage />} />
          </Route>
          <Route path="closings">
            <Route index element={<ClosingListPage />} />
            <Route path="new" element={<ClosingNewPage />} />
            <Route path=":id" element={<ClosingDetailPage />} />
            {/* 請求一覧タブ。締め対象一覧(:id)とURLを分ける (#6136)。 */}
            <Route path=":id/invoices" element={<ClosingDetailPage />} />
          </Route>
          <Route path=":id" element={<OrderEditPage />} />
          <Route path=":id/email" element={<OrderEmailPage />} />
          <Route path=":id/shipments" element={<OrderShipmentsRedirect />} />
          <Route path=":id/shipments/:shipmentId" element={<OrderShipmentsRedirect />} />
          <Route path=":id/shipments/:shipmentId/email" element={<DeliveryNoteEmailPage />} />
        </Route>
        <Route path="manufacturing-orders">
          <Route index element={<ManufacturingOrderListPage />} />
          <Route path="new" element={<ManufacturingOrderCreatePage />} />
          <Route path=":id" element={<ManufacturingOrderEditPage />} />
        </Route>
        <Route path="purchase-orders">
          <Route index element={<PurchaseOrderListPage />} />
          <Route path="items" element={<PurchaseOrderItemListPage />} />
          <Route path="arrivals" element={<ArrivalsListPage />} />
          {/* 仕入一覧(入荷済情報ベース)。可視性は PurchaseOrderTabs の UI 機能フラグで制御する (#6291)。 */}
          <Route path="purchases" element={<PurchaseListPage />} />
          <Route path="purchases/items" element={<PurchaseItemListPage />} />
          {/* 仕入締め。可視性は PurchaseOrderTabs の UI 機能フラグで制御する (#6338, #6339)。 */}
          <Route path="closings">
            <Route index element={<PurchaseClosingListPage />} />
            <Route path="new" element={<PurchaseClosingNewPage />} />
            <Route path=":id" element={<PurchaseClosingDetailPage />} />
            {/* 支払請求一覧タブ。締め対象一覧(:id)とURLを分ける。 */}
            <Route path=":id/invoices" element={<PurchaseClosingDetailPage />} />
          </Route>
          <Route path="import" element={<PurchaseOrderImportPage />} />
          <Route path=":id" element={<PurchaseOrderEditPage />} />
          <Route path=":id/fax" element={<PurchaseOrderFaxPage />} />
          <Route path=":id/email" element={<PurchaseOrderEmailPage />} />
          <Route path="purchase-invoices">
            <Route index element={<PurchaseInvoiceListPage />} />
            <Route path="consolidate" element={<ConsolidatePurchaseOrdersInvoicePage />} />
            <Route path="import" element={<PurchaseInvoiceImportPage />} />
            <Route path=":id" element={<PurchaseInvoiceDetailPage />} />
          </Route>
          <Route path="accounts-payable">
            <Route index element={<AccountsPayableListPage />} />
            <Route path="by-category" element={<AccountsPayableByCategoryPage />} />
            <Route path=":id" element={<AccountsPayableDetailPage />} />
          </Route>
          <Route path="outgoing-payments">
            <Route index element={<OutgoingPaymentListPage />} />
            <Route path="import" element={<OutgoingPaymentImportPage />} />
            <Route path=":id" element={<OutgoingPaymentDetailPage />} />
          </Route>
        </Route>
        <Route path="inventory">
          <Route index element={<InventoryListPage />} />
          {isEnabledFeature(FeatureFlagType.InventoryLot) && (
            <Route path="lots/:id?" element={<LotListPage />} />
          )}
          <Route path="stocktaking">
            <Route index element={<StocktakingListPage />} />
            <Route path="new" element={<StocktakingNewPage />} />
            <Route path=":id" element={<StocktakingEditPage />} />
          </Route>
          <Route path="stock-adjustment">
            <Route index element={<StockAdjustmentListPage />} />
            <Route path="items" element={<StockAdjustmentItemListPage />} />
            <Route path=":id" element={<StockAdjustmentDetailPage />} />
          </Route>
        </Route>
        <Route path="customers">
          <Route index element={<CustomerBetaListPage />} />
          <Route path="new" element={<CustomerCreatePage />} />
          <Route path="import" element={<CustomerImportPage />} />
          <Route path="activities" element={<CustomerActivitiesListPage />} />
          <Route path="activities/import" element={<PartnerActivityImportPage />} />
          <Route path="contact-persons" element={<ContactPersonListPage />} />
          <Route path="contact-persons/import" element={<ContactPersonImportPage />} />
          <Route path="contact-persons/new" element={<ContactPersonCreatePage />} />
          <Route path="contact-persons/:id" element={<ContactPersonDetailPage />} />
          <Route path="contact-persons/:id/edi-account-info" element={<ContactPersonLoginInfoPage />} />
          <Route path=":id" element={<CustomerDetailPage />} />
          <Route path=":id/customer-info" element={<CustomerInfoPage />} />
          <Route
            path=":id/customer-info/delivery-destinations"
            element={<CustomerDeliveryDestinationsPage />}
          />
          <Route path=":id/customer-info/billing-settings" element={<CustomerBillingSettingsPage />} />
          <Route path=":id/customer-info/delivery-note-email" element={<CustomerDeliveryNoteEmailPage />} />
          <Route path=":id/supplier-info" element={<CustomerSupplierInfoPage />} />
          <Route path=":id/supplier-info/bank-accounts" element={<CustomerSupplierBankAccountsPage />} />
          <Route path=":id/supplier-info/payment" element={<CustomerSupplierPaymentPage />} />
          <Route path=":id/documents" element={<CustomerDocumentsPage />} />
          <Route path=":id/product-prices" element={<CustomerProductPricesPage />} />
          <Route path=":id/customer-model-numbers" element={<CustomerModelNumbersPage />} />
          <Route path=":id/document-templates" element={<CustomerDocumentTemplatesRedirect />} />
          <Route
            path=":id/document-templates/purchase-order"
            element={<CustomerPurchaseOrderTemplatesPage />}
          />
          <Route
            path=":id/document-templates/delivery-note"
            element={<CustomerDeliveryNoteTemplatesPage />}
          />
          <Route path=":id/contact-persons" element={<CustomerContactPersonsPage />} />
          <Route path=":id/activities" element={<CustomerActivitiesPage />} />
        </Route>
        <Route path="settings">
          <Route index element={<Navigate to="/settings/organization" replace />} />
          <Route path="members">
            <Route index element={<MemberListPage />} />
            <Route path=":id" element={<MemberEditPage />} />
            <Route path="departments">
              <Route index element={<DepartmentListPage />} />
              <Route path="new" element={<DepartmentCreatePage />} />
              <Route path=":id" element={<DepartmentEditPage />} />
            </Route>
          </Route>
          <Route path="organization" element={<OrganizationSettingsLayout />}>
            <Route index element={<OrganizationBasicInfoPage />} />
            <Route path="profit-margin" element={<ProfitMarginPage />} />
            <Route path="header-menus" element={<HeaderMenuSettingsPage />} />
          </Route>
          <Route path="locations">
            <Route index element={<LocationSettingsPage />} />
            <Route path="warehouses">
              <Route index element={<Navigate to="/settings/locations" replace />} />
              <Route path="new" element={<WarehouseNewPage />} />
              <Route path=":id" element={<WarehouseEditPage />} />
            </Route>
            {/* 棚番マスタ: kind=floors|areas|shelves */}
            <Route path=":kind/new" element={<ShelfMasterNewPage />} />
            <Route path=":kind/:id" element={<ShelfMasterEditPage />} />
          </Route>
          <Route path="invoices">
            <Route index element={<InvoiceSettingsPage />} />
            <Route path="bank-accounts">
              {/* Breadcrumbsの実装の制約でリダイレクトしている */}
              <Route index element={<Navigate to="/settings/invoices" replace />} />
              <Route path="new" element={<BankAccountCreatePage />} />
              <Route path=":id" element={<BankAccountEditPage />} />
            </Route>
            <Route path="payment-methods">
              <Route index element={<PaymentMethodListPage />} />
              <Route path="new" element={<PaymentMethodNewPage />} />
              <Route path=":id" element={<PaymentMethodEditPage />} />
            </Route>
            <Route path="tags">
              <Route index element={<InvoiceTagSettingsPage />} />
              <Route path="new" element={<InvoiceTagCreatePage />} />
              <Route path=":id" element={<InvoiceTagEditPage />} />
            </Route>
            <Route path="export-settings">
              <Route index element={<InvoiceExportSettingsListPage />} />
              <Route path="new" element={<InvoiceExportSettingsNew />} />
              <Route path=":id" element={<InvoiceExportSettingsEdit />} />
            </Route>
            <Route path="invoice-templates" element={<InvoiceTemplateSettingPage />} />
            <Route path="email-template" element={<InvoiceEmailTemplateSettingPage />} />
          </Route>
          <Route path="sales-orders">
            <Route index element={<Navigate to="/settings/sales-orders/basic-settings" replace />} />
            <Route path="basic-settings" element={<SalesOrderBasicSettingsPage />} />
            <Route path="custom-properties">
              <Route index element={<SalesOrderSettingsPage />} />
              <Route path="new" element={<SalesOrderCustomPropertyCreatePage />} />
              <Route path=":id" element={<SalesOrderCustomPropertyEditPage />} />
            </Route>
            <Route path="export-settings">
              <Route index element={<OrderExportSettingsListPage />} />
              <Route path="new" element={<OrderExportSettingsNew />} />
              <Route path=":id" element={<OrderExportSettingsEdit />} />
            </Route>
            <Route path="priorities">
              <Route index element={<SalesOrderPriorityListPage />} />
              <Route path="new" element={<SalesOrderPriorityNewPage />} />
              <Route path=":id" element={<SalesOrderPriorityEditPage />} />
            </Route>
            <Route path="email-template" element={<DeliveryNoteEmailTemplateSettingPage />} />
            <Route path="sales-order-templates" element={<SalesOrderTemplateSettingPage />} />
            <Route path="delivery-note-templates" element={<DeliveryNoteTemplateSettingPage />} />
          </Route>
          <Route path="leads">
            <Route index element={<Navigate to="/settings/leads/tags" replace />} />
            <Route path="custom-properties">
              <Route index element={<LeadCustomPropertyListPage />} />
              <Route path="new" element={<LeadCustomPropertyCreatePage />} />
              <Route path=":id" element={<LeadCustomPropertyEditPage />} />
            </Route>
            <Route path="tags">
              <Route index element={<LeadTagSettingsPage />} />
              <Route path="new" element={<LeadTagCreatePage />} />
              <Route path="import" element={<LeadTagImportPage />} />
              <Route path=":id" element={<LeadTagEditPage />} />
            </Route>
            <Route path="company-name-patterns">
              <Route index element={<CompanyNamePatternListPage />} />
              <Route path="new" element={<CompanyNamePatternCreatePage />} />
              <Route path=":id" element={<CompanyNamePatternEditPage />} />
            </Route>
          </Route>
          <Route path="user" element={<UserSettingsPage />} />
          <Route path="item-display">
            <Route index element={<ItemDisplaySettingsPage />} />
          </Route>
          <Route path="manufacturing-orders">
            <Route
              index
              element={<Navigate to="/settings/manufacturing-orders/custom-properties" replace />}
            />
            <Route path="custom-properties">
              <Route index element={<ManufacturingOrderCustomPropertyListPage />} />
              <Route path="new" element={<ManufacturingOrderCustomPropertyCreatePage />} />
              <Route path=":id" element={<ManufacturingOrderCustomPropertyEditPage />} />
            </Route>
          </Route>
          <Route path="purchase-orders">
            <Route index element={<Navigate to="/settings/purchase-orders/labels" replace />} />
            <Route path="purchase-order-templates" element={<PurchaseOrderSettingsPage />} />
            <Route path="email-template" element={<PurchaseOrderEmailTemplateSettingPage />} />
            <Route path="labels">
              <Route index element={<PurchaseOrderLabelListPage />} />
              <Route path="new" element={<PurchaseOrderLabelNewPage />} />
              <Route path=":id" element={<PurchaseOrderLabelEditPage />} />
            </Route>
            <Route path="export-settings">
              <Route index element={<PurchaseOrderExportSettingsListPage />} />
              <Route path="new" element={<PurchaseOrderExportSettingsNew />} />
              <Route path=":id" element={<PurchaseOrderExportSettingsEdit />} />
            </Route>
            <Route path="custom-properties">
              <Route index element={<PurchaseOrderCustomPropertyListPage />} />
              <Route path="new" element={<PurchaseOrderCustomPropertyCreatePage />} />
              <Route path=":id" element={<PurchaseOrderCustomPropertyEditPage />} />
            </Route>
          </Route>
          <Route path="document-settings">
            <Route index element={<Navigate to="/settings/document-settings/stamp-seal" replace />} />
            <Route path="stamp-seal" element={<DocumentSettingsPage />} />
            <Route path="logo" element={<DocumentLogoSettingsPage />} />
            <Route path="notes-templates">
              <Route index element={<NotesTemplatesListPage />} />
              <Route path="new" element={<NotesTemplateNewPage />} />
              <Route path=":id" element={<NotesTemplateEditPage />} />
            </Route>
          </Route>
          <Route path="edi">
            <Route index element={<Navigate to="/settings/edi/notifications" replace />} />
            <Route path="notifications" element={<EdiNotificationSettingsPage />} />
            <Route path="display" element={<EdiDisplaySettingsPage />} />
            <Route path="notices">
              <Route index element={<EdiNoticeListPage />} />
              <Route path="new" element={<EdiNoticeCreatePage />} />
              <Route path=":id" element={<EdiNoticeEditPage />} />
            </Route>
          </Route>
          <Route path="quotations">
            <Route index element={<Navigate to="/settings/quotations/custom-properties" replace />} />
            <Route path="custom-properties">
              <Route index element={<QuotationCustomPropertyListPage />} />
              <Route path="new" element={<QuotationCustomPropertyCreatePage />} />
              <Route path=":id" element={<QuotationCustomPropertyEditPage />} />
            </Route>
            <Route path="quotation-templates" element={<QuotationTemplateSettingPage />} />
          </Route>
          <Route path="projects">
            <Route index element={<ProjectStatusSettingsPage />} />
            <Route path="new" element={<ProjectStatusNewPage />} />
            <Route path=":id" element={<ProjectStatusEditPage />} />
            <Route path="order-accuracies">
              <Route index element={<OrderAccuracySettingsPage />} />
              <Route path="new" element={<OrderAccuracyNewPage />} />
              <Route path=":id" element={<OrderAccuracyEditPage />} />
            </Route>
            <Route path="custom-properties">
              <Route index element={<ProjectCustomPropertyListPage />} />
              <Route path="new" element={<ProjectCustomPropertyCreatePage />} />
              <Route path=":id" element={<ProjectCustomPropertyEditPage />} />
            </Route>
          </Route>
          <Route path="lead-sources">
            <Route index element={<LeadSourceSettingsPage />} />
            <Route path="new" element={<LeadSourceNewPage />} />
            <Route path=":id" element={<LeadSourceEditPage />} />
          </Route>
          <Route path="security">
            <Route index element={<Navigate to="/settings/security/password" replace />} />
            <Route path="password" element={<PasswordChangePage />} />
            <Route path="mfa" element={<MfaSettingsPage />} />
            <Route path="audit-logs" element={<AuditLogListPage />} />
          </Route>
          <Route path="api" element={<ApiSettingsPage />} />
          <Route path="warehouse-app" element={<WarehouseAppSettingsPage />} />
          <Route path="products">
            <Route path="package-types">
              <Route index element={<PackageTypeListPage />} />
              <Route path="new" element={<PackageTypeCreatePage />} />
              <Route path=":id" element={<PackageTypeEditPage />} />
            </Route>
            <Route path="categories">
              <Route index element={<ProductCategoryListPage />} />
              <Route path="new" element={<ProductCategoryCreatePage />} />
              <Route path=":id" element={<ProductCategoryEditPage />} />
            </Route>
            <Route path="price-revision-custom-properties">
              <Route index element={<PriceRevisionCustomPropertyListPage />} />
              <Route path="new" element={<PriceRevisionCustomPropertyCreatePage />} />
              <Route path=":id" element={<PriceRevisionCustomPropertyEditPage />} />
            </Route>
            <Route path="custom-properties">
              <Route index element={<CatalogCustomPropertyListPage />} />
              <Route path="new" element={<CatalogCustomPropertyCreatePage />} />
              <Route path="import" element={<CatalogCustomPropertyImportPage />} />
              <Route path="groups">
                <Route index element={<CatalogCustomPropertyGroupListPage />} />
                <Route path="new" element={<CatalogCustomPropertyGroupCreatePage />} />
                <Route path=":id" element={<CatalogCustomPropertyGroupEditPage />} />
              </Route>
              <Route path=":id" element={<CatalogCustomPropertyEditPage />} />
            </Route>
            <Route index element={<UnitListPage />} />
            <Route path="new" element={<UnitCreatePage />} />
            <Route path=":id" element={<UnitEditPage />} />
          </Route>
          <Route path="partners">
            <Route index element={<Navigate to="/settings/partners/amount-calculation" replace />} />
            <Route path="amount-calculation" element={<OrganizationAmountCalculationSettingsPage />} />
            <Route path="categories">
              <Route index element={<PartnerCategoryListPage />} />
              <Route path="new" element={<PartnerCategoryCreatePage />} />
              <Route path=":id" element={<PartnerCategoryEditPage />} />
            </Route>
            <Route path="price-groups" element={<PartnerPriceGroupSettingsPage />} />
            <Route path="tags">
              <Route index element={<PartnerTagSettingsPage />} />
              <Route path="new" element={<PartnerTagCreatePage />} />
              <Route path=":id" element={<PartnerTagEditPage />} />
            </Route>
            <Route path="delivery-destination-labels">
              <Route index element={<DeliveryDestinationLabelSettingsPage />} />
              <Route path="new" element={<DeliveryDestinationLabelCreatePage />} />
              <Route path=":id" element={<DeliveryDestinationLabelEditPage />} />
            </Route>
            <Route path="custom-properties">
              <Route index element={<PartnerCustomPropertyListPage />} />
              <Route path="new" element={<PartnerCustomPropertyCreatePage />} />
              <Route path="import" element={<PartnerCustomPropertyImportPage />} />
              <Route path="groups">
                <Route index element={<PartnerCustomPropertyGroupListPage />} />
                <Route path="new" element={<PartnerCustomPropertyGroupCreatePage />} />
                <Route path=":id" element={<PartnerCustomPropertyGroupEditPage />} />
              </Route>
              <Route path=":id" element={<PartnerCustomPropertyEditPage />} />
            </Route>
            <Route path="export-settings">
              <Route index element={<PartnerExportSettingsListPage />} />
              <Route path="new" element={<PartnerExportSettingsNew />} />
              <Route path=":id" element={<PartnerExportSettingsEdit />} />
            </Route>
            <Route path="closing-groups">
              <Route index element={<ClosingGroupListPage />} />
              <Route path="new" element={<ClosingGroupCreatePage />} />
              <Route path=":id" element={<ClosingGroupEditPage />} />
            </Route>
            <Route path="activity-types">
              <Route index element={<PartnerActivityTypeSettingsPage />} />
              <Route path="new" element={<PartnerActivityTypeCreatePage />} />
              <Route path=":id" element={<PartnerActivityTypeEditPage />} />
            </Route>
            <Route path="activity-custom-properties">
              <Route index element={<PartnerActivityCustomPropertyListPage />} />
              <Route path="new" element={<PartnerActivityCustomPropertyCreatePage />} />
              <Route path=":id" element={<PartnerActivityCustomPropertyEditPage />} />
            </Route>
          </Route>
          <Route path="inventory">
            <Route index element={<InventorySettingsPage />} />
            <Route path="custom-properties">
              <Route index element={<StockAdjustmentCustomPropertyListPage />} />
              <Route path="new" element={<StockAdjustmentCustomPropertyCreatePage />} />
              <Route path=":id" element={<StockAdjustmentCustomPropertyEditPage />} />
            </Route>
          </Route>
          <Route path="payments">
            <Route index element={<Navigate to="/settings/payments/account-titles" replace />} />
            <Route path="account-titles">
              <Route index element={<AccountTitleListPage />} />
              <Route path="new" element={<AccountTitleNewPage />} />
              <Route path=":id" element={<AccountTitleEditPage />} />
            </Route>
          </Route>
          <Route path="external-integrations">
            <Route index element={<ExternalIntegrationsSettingsPage />} />
            <Route path="new" element={<ExternalIntegrationsNewPage />} />
            <Route path=":id" element={<ExternalIntegrationsEditPage />} />
          </Route>
        </Route>
      </Route>
    </Routes>
  );
};

const App = () => {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppVersionGate />
        <AppRoutes />
        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme="colored"
        />
      </AuthProvider>
    </BrowserRouter>
  );
};

export default App;
